import { Fragment, ReactNode, useState } from 'react'
import cx from 'classnames'
import isEmpty from 'lodash/isEmpty'

import { ChevronRight } from 'common/icons'

export interface TreeNode {
  [key: string]: TreeNode | string[]
}

const MenuItem = ({
  children,
  onSelect,
  className,
}: {
  onSelect?: () => void
  className?: string
  children: ReactNode
}) => (
  <button
    onClick={onSelect}
    className={cx(
      className,
      cx(
        'pl-6 pr-4 py-4 border border-coolGray-200 focus-visible:border-transparent focus:outline-none block w-full',
        'border-b-0 border-l-0 first:border-t-0 last:border-b-1'
      )
    )}
  >
    {children}
  </button>
)

const RecursiveSubMenu = ({
  items,
  selectedPath,
  onSelect,
  depth,
}: {
  items: TreeNode | string[]
  selectedPath: string[]
  onSelect: (item: string, depth: number) => void
  depth: number
}) => {
  if ((depth !== 0 && selectedPath.length < depth) || items.length === 0) {
    return null
  }
  return (
    <>
      <div className="overflow-y-auto overflow-x-hidden h-full w-100">
        {Array.isArray(items) ? (
          <div>
            {items.map((item, index) => (
              <MenuItem
                key={index}
                className="hover:bg-blueGray-200 hover:font-medium cursor-pointer truncate text-left"
              >
                {item}
              </MenuItem>
            ))}
          </div>
        ) : (
          Reflect.ownKeys(items).map((item) => {
            return (
              <Fragment key={item as string}>
                <MenuItem
                  className={cx(
                    'hover:bg-blueGray-200 cursor-pointer hover:font-medium w-full break-words',
                    {
                      'bg-blueGray-200 font-medium':
                        selectedPath[depth] === item,
                    }
                  )}
                  onSelect={() => {
                    onSelect(item as string, depth)
                  }}
                >
                  <div className="flex justify-between gap-4 w-full">
                    <div className="w-11/12 truncate text-left">{item}</div>
                    <ChevronRight
                      className={cx({ hidden: isEmpty(items[item as string]) })}
                      size={4}
                    />
                  </div>
                </MenuItem>
              </Fragment>
            )
          })
        )}
      </div>

      <RecursiveSubMenu
        items={items[selectedPath[depth]]}
        onSelect={onSelect}
        selectedPath={selectedPath}
        depth={depth + 1}
      />
    </>
  )
}

type Props = {
  items: TreeNode
}

const Catalog = ({ items }: Props) => {
  const [selectedPath, setSelectedPath] = useState<string[]>([])

  return (
    <div className="w-full overflow-auto">
      <div className="bg-blueGray-100 flex w-max h-full box-border border border-coolGray-200">
        <RecursiveSubMenu
          items={items}
          selectedPath={selectedPath}
          onSelect={(item: string, depth: number) => {
            if (depth === 0) {
              setSelectedPath([item])
            } else {
              setSelectedPath((prev) => [...prev.slice(0, depth), item])
            }
          }}
          depth={0}
        />
      </div>
    </div>
  )
}

export default Catalog
